import type { MeasurementAnswerInput } from '@/src/queries/measurement-queries'

import type {
  VoiceAssessmentAction,
  VoiceAssessmentState,
} from './VoiceAssessmentFlow.shared'
import {
  createInitialMeasurementBlobs,
  createInitialMeasurementQuestionAnswers,
  createInitialMeasurementUris,
  createInitialPersistedMeasurementIds,
} from './VoiceAssessmentFlow.shared'

export function buildInitialVoiceAssessmentState(
  measurementCount: number,
): VoiceAssessmentState {
  return {
    started: true,
    measurementIndex: 0,
    measurementPhase: 'instructions',
    personalReflection: '',
    confirmedRecordingUris: createInitialMeasurementUris(measurementCount),
    confirmedRecordingBlobs: createInitialMeasurementBlobs(measurementCount),
    persistedMeasurementIds:
      createInitialPersistedMeasurementIds(measurementCount),
    measurementQuestionAnswers:
      createInitialMeasurementQuestionAnswers(measurementCount),
  }
}

function setSingleChoiceAnswer(
  answers: MeasurementAnswerInput[],
  questionId: number,
  choiceIndex: number,
): MeasurementAnswerInput[] {
  const nextAnswer = { questionId, choiceIndices: [choiceIndex] }
  const existingIndex = answers.findIndex(
    (answer) => answer.questionId === questionId,
  )
  if (existingIndex === -1) {
    return [...answers, nextAnswer]
  }
  return answers.map((answer, index) =>
    index === existingIndex ? nextAnswer : answer,
  )
}

function toggleMultipleChoiceAnswer(
  answers: MeasurementAnswerInput[],
  questionId: number,
  choiceIndex: number,
): MeasurementAnswerInput[] {
  const existing = answers.find((answer) => answer.questionId === questionId)
  const choiceIndices = new Set(existing?.choiceIndices ?? [])

  if (choiceIndices.has(choiceIndex)) {
    choiceIndices.delete(choiceIndex)
  } else {
    choiceIndices.add(choiceIndex)
  }

  const nextAnswer = {
    questionId,
    choiceIndices: Array.from(choiceIndices).sort((a, b) => a - b),
  }
  if (existing == null) {
    return [...answers, nextAnswer]
  }
  return answers.map((answer) =>
    answer.questionId === questionId ? nextAnswer : answer,
  )
}

function updateMeasurementQuestionAnswers(
  state: VoiceAssessmentState,
  index: number,
  updater: (answers: MeasurementAnswerInput[]) => MeasurementAnswerInput[],
): VoiceAssessmentState {
  if (index < 0) {
    return state
  }
  const next = [...state.measurementQuestionAnswers]
  while (next.length <= index) {
    next.push([])
  }
  next[index] = updater(next[index] ?? [])
  return { ...state, measurementQuestionAnswers: next }
}

function resizeArray<T>(values: T[], count: number, factory: () => T): T[] {
  if (values.length === count) {
    return values
  }
  if (count < values.length) {
    return values.slice(0, count)
  }
  const next = [...values]
  while (next.length < count) {
    next.push(factory())
  }
  return next
}

export function makeVoiceAssessmentReducer(initialMeasurementCount: number) {
  return function voiceAssessmentReducer(
    state: VoiceAssessmentState,
    action: VoiceAssessmentAction,
  ): VoiceAssessmentState {
    switch (action.type) {
      case 'set_measurement_index':
        return {
          ...state,
          measurementIndex: action.index,
          measurementPhase: 'instructions',
        }
      case 'set_measurement_phase':
        return { ...state, measurementPhase: action.phase }
      case 'set_personal_reflection':
        return { ...state, personalReflection: action.value }
      case 'set_measurement_confirmed_recording': {
        if (
          action.index < 0 ||
          action.index >= state.confirmedRecordingUris.length
        ) {
          return state
        }
        const nextUris = [...state.confirmedRecordingUris]
        const nextBlobs = [...state.confirmedRecordingBlobs]
        nextUris[action.index] = action.uri
        nextBlobs[action.index] = action.blob
        return {
          ...state,
          confirmedRecordingUris: nextUris,
          confirmedRecordingBlobs: nextBlobs,
          measurementPhase:
            action.uri == null ? 'record' : state.measurementPhase,
        }
      }
      case 'set_measurement_persisted_id': {
        if (
          action.index < 0 ||
          action.index >= state.persistedMeasurementIds.length
        ) {
          return state
        }
        const next = [...state.persistedMeasurementIds]
        next[action.index] = action.value
        return { ...state, persistedMeasurementIds: next }
      }
      case 'set_measurement_single_choice_answer':
        return updateMeasurementQuestionAnswers(
          state,
          action.index,
          (answers) =>
            setSingleChoiceAnswer(
              answers,
              action.questionId,
              action.choiceIndex,
            ),
        )
      case 'toggle_measurement_multiple_choice_answer':
        return updateMeasurementQuestionAnswers(
          state,
          action.index,
          (answers) =>
            toggleMultipleChoiceAnswer(
              answers,
              action.questionId,
              action.choiceIndex,
            ),
        )
      case 'resize_measurements': {
        const count = Math.max(0, action.count)
        return {
          ...state,
          confirmedRecordingUris: resizeArray(
            state.confirmedRecordingUris,
            count,
            () => null,
          ),
          confirmedRecordingBlobs: resizeArray(
            state.confirmedRecordingBlobs,
            count,
            () => null,
          ),
          persistedMeasurementIds: resizeArray(
            state.persistedMeasurementIds,
            count,
            () => null,
          ),
          measurementQuestionAnswers: resizeArray(
            state.measurementQuestionAnswers,
            count,
            () => [],
          ),
        }
      }
      case 'hydrate':
        return {
          ...buildInitialVoiceAssessmentState(initialMeasurementCount),
          ...action.payload,
        }
      default:
        return state
    }
  }
}
